import React from "react";
import { Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { Table, TD, TH, TR } from "@ag-media/react-pdf-table";
import _ from "lodash";
import { formatCurrencyUSD } from "../../../utils/formattingUtils";
import {
    getFooter,
    salesAdjustmentsTableConfig,
    salesCompsTableConfig,
    salesReviewIndicationTableConfig,
} from "../../../utils/helperUtils";

const styles = StyleSheet.create({
    page: {
        padding: "20px 30px",
        fontSize: 8,
        backgroundColor: "#ffffff",
        position: "relative",
    },
    heading: {
        fontSize: 20,
        fontWeight: "bold",
        marginBottom: 8,
        textAlign: "center",
        color: "#2c3e50",
        paddingBottom: 15,
    },
    subHeading: {
        fontSize: 7,
        fontWeight: "bold",
        marginBottom: 5,
        textAlign: "center",
        color: "#34495e",
    },
    section: {
        marginBottom: 7,
        padding: 5,
    },
    tableCell: {
        textAlign: "left",
        fontSize: 8,
        padding: "3px 1px",
        borderBottom: "1px solid #e0e0e0",
        whiteSpace: "wrap",
        wordWrap: "break-word",
    },
    tableHeader: {
        backgroundColor: "#f7f6f8",
        fontSize: 14,
        padding: "4px 3px",
    },
    mainContainer: {
        flex: 1,
        paddingBottom: 20,
    },
    indicatedValue: {
        marginTop: 8,
        padding: 8,
        backgroundColor: "#f7f6f8",
        borderRadius: 2,
        textAlign: "center",
    },
    indicatedValueText: {
        fontSize: 12,
        color: "#2c3e50",
        fontWeight: "bold",
        padding: 5,
    },
});

const TableHeaderRow = ({ compsLength }) => (
    <TR>
        <TD style={{ ...styles.tableCell, fontSize: 10 }}>Property Details</TD>
        {[...Array(compsLength)].map((_, index) => (
            <TD key={index} style={{ ...styles.tableCell, fontSize: 10 }}>
                Comp #{index + 1}
            </TD>
        ))}
    </TR>
);

const AdjustmentTableHeaderRow = ({ compsLength }) => (
    <TR>
        <TD style={{ ...styles.tableCell, fontSize: 10, minWidth: "15%" }}>Property Details</TD>
        {[...Array(compsLength)].map((_, index) => (
            <TD
                key={index}
                style={{
                    ...styles.tableCell,
                    fontSize: 10,
                }}
            >
                Comp #{index + 1}
            </TD>
        ))}
    </TR>
);

const TableRow = ({ rowData }) => (
    <TR>
        {rowData.map((cell, index) => (
            <TD key={index} style={styles.tableCell}>
                {cell}
            </TD>
        ))}
    </TR>
);

const AdjustmentTableRow = ({ rowData }) => (
    <TR>
        {rowData.map((cell, index) => (
            <TD key={index} style={{ ...styles.tableCell, minWidth: index === 0 ? "15%" : "auto" }}>
                {cell}
            </TD>
        ))}
    </TR>
);

export const SalesComparablesPage = ({ data, confidentialInfo }) => {
    const comps = _.get(data, "appealApproachCalculationData.sales.comps", {});
    const enabledComps = Object.keys(comps)
        .map(key => comps[key])
        .filter(comp => comp.enable === true);
    const summary = _.get(data, "appealApproachCalculationData.sales.summary", {});

    return (
        <>
            <Page size="A4" style={styles.page}>
                <View style={styles.mainContainer}>
                    <Text style={styles.heading}>Comparable Sales Approach</Text>
                    <View style={styles.section}>
                        <Table style={{ border: "none" }}>
                            <TH>
                                <TD
                                    style={{
                                        ...styles.tableCell,
                                        ...styles.tableHeader,
                                        width: "100%",
                                        textAlign: "left",
                                        borderRight: "none",
                                    }}
                                >
                                    Comparable Sales Grid & Adjustments
                                </TD>
                            </TH>
                            <TableHeaderRow compsLength={enabledComps.length} />
                            {salesCompsTableConfig.rowResources.getRows(data).map((row, index) => (
                                <TableRow key={index} rowData={row} />
                            ))}
                        </Table>
                    </View>

                    {getFooter(confidentialInfo)}
                </View>
            </Page>
            <Page size="A4" style={styles.page}>
                <View style={styles.mainContainer}>
                    <View style={styles.section}>
                        <Table style={{ border: "none" }}>
                            <TH>
                                <TD
                                    style={{
                                        ...styles.tableCell,
                                        ...styles.tableHeader,
                                        width: "100%",
                                        textAlign: "left",
                                        borderRight: "none", // Remove right border
                                    }}
                                >
                                    Adjustments
                                </TD>
                            </TH>
                            <AdjustmentTableHeaderRow compsLength={enabledComps.length} />
                            {salesAdjustmentsTableConfig.rowResources
                                .getRows(data)
                                .map((row, index) => (
                                    <AdjustmentTableRow key={index} rowData={row} />
                                ))}
                        </Table>
                    </View>

                    <View style={styles.section}>
                        <Table style={{ border: "none" }}>
                            <TH>
                                <TD
                                    style={{
                                        ...styles.tableCell,
                                        ...styles.tableHeader,
                                        width: "100%",
                                        textAlign: "left",
                                        borderRight: "none", // Remove right border
                                    }}
                                >
                                    Sales Review Indication
                                </TD>
                            </TH>
                            <TR>
                                {salesReviewIndicationTableConfig.headers.map((header, index) => (
                                    <TD key={index} style={{ ...styles.tableCell, fontSize: 10 }}>
                                        {header}
                                    </TD>
                                ))}
                            </TR>
                            <TR>
                                {salesReviewIndicationTableConfig.rowResources
                                    .getRows(data)
                                    .map((cell, index) => (
                                        <TD
                                            key={index}
                                            style={{ ...styles.tableCell, fontSize: 10 }}
                                        >
                                            {formatCurrencyUSD(cell)}
                                        </TD>
                                    ))}
                            </TR>
                        </Table>
                    </View>

                    <View style={styles.indicatedValue}>
                        <Text style={styles.heading}>INDICATED VALUE BASED ON COMPS</Text>
                        <Text style={styles.indicatedValueText}>
                            Rate: {formatCurrencyUSD(summary.indicatedValue.indicatedRate)} | Value:{" "}
                            {formatCurrencyUSD(summary.indicatedValue.indicatedValue)}
                        </Text>
                    </View>

                    {getFooter(confidentialInfo)}
                </View>
            </Page>
        </>
    );
};
